import {
	redirect,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
} from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { isUserIdAdmin } from "@/lib/userRoles";

const ITEMS_PER_PAGE = 20;

type DatasetRow = Database["public"]["Tables"]["test_data_sets"]["Row"];
type TestDataEntryRow =
	Database["public"]["Tables"]["test_data_entries"]["Row"];

export type TestDataViewActionResult =
	| { ok: true; intent: string; datasetId?: string; entryId?: string }
	| { ok: false; intent?: string; error: string };

type LoaderResult = {
	datasets: DatasetRow[];
	selectedDatasetId: string | null;
	selectedDataset: DatasetRow | null;
	entries: TestDataEntryRow[];
	parentEntries: TestDataEntryRow[];
	pagination: { page: number; totalPages: number; totalCount: number };
	lockState: { isLocked: boolean; lockReason: string };
	userIsAdmin: boolean;
};

const ensureSession = async () => {
	const {
		data: { session },
		error,
	} = await supabase.auth.getSession();

	if (error || !session?.user) {
		throw redirect("/auth");
	}

	return session.user;
};

const fetchDataset = async (datasetId: string) => {
	const { data, error } = await supabase
		.from("test_data_sets")
		.select("*")
		.eq("id", datasetId)
		.maybeSingle();

	if (error) {
		throw error;
	}

	if (!data) {
		return null;
	}

	return data;
};

export const testDataViewLoader = async ({
	request,
}: LoaderFunctionArgs): Promise<LoaderResult> => {
	const user = await ensureSession();
	const url = new URL(request.url);
	const datasetParam = url.searchParams.get("datasetId");
	const requestedPage = Number(url.searchParams.get("page") ?? "1");
	const page =
		Number.isNaN(requestedPage) || requestedPage < 1
			? 1
			: Math.floor(requestedPage);

	// Check if user is admin
	const userIsAdmin = await isUserIdAdmin(user.id);

	// If admin, fetch all datasets; otherwise only user's own datasets
	const { data: datasets, error: datasetsError } = userIsAdmin
		? await supabase
				.from("test_data_sets")
				.select("*")
				.order("created_at", { ascending: false })
		: await supabase
				.from("test_data_sets")
				.select("*")
				.eq("user_id", user.id)
				.order("created_at", { ascending: false });

	if (datasetsError) {
		throw datasetsError;
	}

	const selectedDatasetId =
		datasetParam && datasets?.some((set) => set.id === datasetParam)
			? datasetParam
			: (datasets?.[0]?.id ?? null);

	let selectedDataset: DatasetRow | null = null;
	let entries: TestDataEntryRow[] = [];
	let parentEntries: TestDataEntryRow[] = [];
	let totalCount = 0;
	let totalPages = 1;
	let lockState = { isLocked: false, lockReason: "" };

	if (selectedDatasetId) {
		selectedDataset =
			datasets?.find((set) => set.id === selectedDatasetId) ?? null;

		const from = (page - 1) * ITEMS_PER_PAGE;
		const to = from + ITEMS_PER_PAGE - 1;

		const [entriesRes, derivativesRes, testRunsRes, parentEntriesRes] =
			await Promise.all([
				supabase
					.from("test_data_entries")
					.select("*", { count: "exact" })
					.eq("test_data_set_id", selectedDatasetId)
					.order("created_at", { ascending: false })
					.range(from, to),
				supabase
					.from("test_data_sets")
					.select("id")
					.eq("parent_id", selectedDatasetId),
				supabase
					.from("test_runs")
					.select("id", { count: "exact", head: true })
					.eq("test_data_set_id", selectedDatasetId),
				selectedDataset?.type === "adversarial" && selectedDataset.parent_id
					? supabase
							.from("test_data_entries")
							.select("*")
							.eq("test_data_set_id", selectedDataset.parent_id)
							.order("created_at", { ascending: false })
					: Promise.resolve({ data: [], error: null }),
			]);

		if (entriesRes.error) {
			throw entriesRes.error;
		}

		entries = entriesRes.data ?? [];
		totalCount = entriesRes.count ?? 0;
		totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

		if ("error" in parentEntriesRes && parentEntriesRes.error) {
			throw parentEntriesRes.error;
		}
		parentEntries =
			"data" in parentEntriesRes
				? (parentEntriesRes.data as TestDataEntryRow[])
				: [];

		const hasDerivatives = (derivativesRes.data ?? []).length > 0;
		const hasTestRuns = (testRunsRes.count ?? 0) > 0;

		if (hasDerivatives && hasTestRuns) {
			lockState = {
				isLocked: true,
				lockReason:
					"This dataset has adversarial datasets and has been used in test runs",
			};
		} else if (hasDerivatives) {
			lockState = {
				isLocked: true,
				lockReason: "This dataset has adversarial datasets",
			};
		} else if (hasTestRuns) {
			lockState = {
				isLocked: true,
				lockReason: "This dataset has been used in test runs",
			};
		}
	}

	return {
		datasets: datasets ?? [],
		selectedDatasetId,
		selectedDataset,
		entries,
		parentEntries,
		pagination: {
			page,
			totalPages,
			totalCount,
		},
		lockState,
		userIsAdmin,
	} satisfies LoaderResult;
};

export const testDataViewAction = async ({
	request,
}: ActionFunctionArgs): Promise<TestDataViewActionResult> => {
	const user = await ensureSession();
	const formData = await request.formData();
	const intent = formData.get("intent");

	if (typeof intent !== "string") {
		return { ok: false, error: "Missing intent" };
	}

	if (intent === "createDataset") {
		const rawName = formData.get("name");
		const name = typeof rawName === "string" ? rawName.trim() : "";

		if (!name) {
			return { ok: false, intent, error: "Dataset name is required" };
		}

		const parentId = formData.get("parentId");
		let parentDataset: DatasetRow | null = null;

		if (typeof parentId === "string" && parentId) {
			parentDataset = await fetchDataset(parentId);
			if (!parentDataset) {
				return { ok: false, intent, error: "Parent dataset not found" };
			}
			if (parentDataset.type !== "primary") {
				return {
					ok: false,
					intent,
					error: "Adversarial datasets must originate from primary datasets",
				};
			}
		}

		const { data, error } = await supabase
			.from("test_data_sets")
			.insert({
				user_id: user.id,
				name,
				type: parentDataset ? "adversarial" : "primary",
				parent_id: parentDataset?.id ?? null,
				dataset_kind: parentDataset?.dataset_kind ?? "behavior_examples",
			})
			.select("id")
			.single();

		if (error || !data) {
			return {
				ok: false,
				intent,
				error: error?.message ?? "Failed to create dataset",
			};
		}

		return { ok: true, intent, datasetId: data.id };
	}

	if (intent === "createEntry") {
		const datasetId = formData.get("datasetId");
		const input = formData.get("input");
		const expectedOutput = formData.get("expectedOutput");
		const ruleText = formData.get("ruleText");
		const parentEntryId = formData.get("parentEntryId");

		if (typeof datasetId !== "string" || !datasetId) {
			return { ok: false, intent, error: "Dataset ID is required" };
		}

		const dataset = await fetchDataset(datasetId);
		if (!dataset) {
			return { ok: false, intent, error: "Dataset not found" };
		}

		if (dataset.dataset_kind === "operational_rules") {
			if (typeof ruleText !== "string" || !ruleText.trim()) {
				return {
					ok: false,
					intent,
					error: "Rule text is required for rules datasets",
				};
			}
			if (input && typeof input === "string" && input.trim()) {
				return {
					ok: false,
					intent,
					error: "Input field should not be used for rules datasets",
				};
			}
		} else {
			if (typeof input !== "string" || !input.trim()) {
				return {
					ok: false,
					intent,
					error: "Input is required for behavior examples datasets",
				};
			}
			if (ruleText && typeof ruleText === "string" && ruleText.trim()) {
				return {
					ok: false,
					intent,
					error: "Rule text should not be used for behavior examples datasets",
				};
			}
		}

		if (dataset.dataset_kind === "behavior_examples") {
			if (typeof expectedOutput !== "string" || !expectedOutput.trim()) {
				return {
					ok: false,
					intent,
					error: "Expected output is required for behavior examples datasets",
				};
			}
		}

		let parentId: string | null = null;

		if (dataset.type === "adversarial") {
			if (typeof parentEntryId !== "string" || !parentEntryId) {
				return {
					ok: false,
					intent,
					error: "Parent entry is required for adversarial datasets",
				};
			}

			const { data: parentEntry, error: parentEntryError } = await supabase
				.from("test_data_entries")
				.select("id")
				.eq("id", parentEntryId)
				.eq("test_data_set_id", dataset.parent_id as string)
				.maybeSingle();

			if (parentEntryError || !parentEntry) {
				return { ok: false, intent, error: "Parent entry not found" };
			}

			parentId = parentEntry.id;
		}

		const entryData: {
			test_data_set_id: string;
			input: string;
			expected_output: string;
			parent_id: string | null;
			rule_text?: string;
		} = {
			test_data_set_id: datasetId,
			input:
				dataset.dataset_kind === "operational_rules"
					? ""
					: (input as string).trim(),
			expected_output:
				dataset.dataset_kind === "operational_rules"
					? ""
					: (expectedOutput as string).trim(),
			parent_id: parentId,
		};

		if (dataset.dataset_kind === "operational_rules") {
			entryData.rule_text = (ruleText as string).trim();
		}

		const { data, error } = await supabase
			.from("test_data_entries")
			.insert(entryData)
			.select("id")
			.single();

		if (error || !data) {
			return {
				ok: false,
				intent,
				error: error?.message ?? "Failed to create entry",
			};
		}

		return { ok: true, intent, entryId: data.id };
	}

	if (intent === "deleteEntry") {
		const entryId = formData.get("entryId");
		const datasetId = formData.get("datasetId");

		if (
			typeof entryId !== "string" ||
			!entryId ||
			typeof datasetId !== "string" ||
			!datasetId
		) {
			return {
				ok: false,
				intent,
				error: "Entry ID and dataset ID are required",
			};
		}

		const dataset = await fetchDataset(datasetId);
		if (!dataset) {
			return { ok: false, intent, error: "Dataset not found" };
		}

		const entryFilter = supabase
			.from("test_data_entries")
			.delete()
			.eq("id", entryId)
			.eq("test_data_set_id", datasetId);

		if (dataset.type === "adversarial") {
			entryFilter.not("parent_id", "is", null);
		}

		const { error: deleteError } = await entryFilter;

		if (deleteError) {
			return {
				ok: false,
				intent,
				error: deleteError.message ?? "Failed to delete entry",
			};
		}

		return { ok: true, intent };
	}

	if (intent === "deleteDataset") {
		const datasetId = formData.get("datasetId");

		if (typeof datasetId !== "string" || !datasetId) {
			return { ok: false, intent, error: "Dataset ID is required" };
		}

		const dataset = await fetchDataset(datasetId);
		if (!dataset) {
			return { ok: false, intent, error: "Dataset not found" };
		}

		const userIsAdmin = await isUserIdAdmin(user.id);
		if (dataset.user_id !== user.id && !userIsAdmin) {
			return {
				ok: false,
				intent,
				error: "You do not have permission to delete this dataset",
			};
		}

		const { data: derivatives, error: derivativesError } = await supabase
			.from("test_data_sets")
			.select("id")
			.eq("parent_id", datasetId);

		if (derivativesError) {
			return {
				ok: false,
				intent,
				error: "Failed to check for adversarial datasets",
			};
		}

		if (derivatives && derivatives.length > 0) {
			return {
				ok: false,
				intent,
				error: "Cannot delete dataset with adversarial datasets",
			};
		}

		const { count: testRunsCount, error: testRunsError } = await supabase
			.from("test_runs")
			.select("id", { count: "exact", head: true })
			.eq("test_data_set_id", datasetId);

		if (testRunsError) {
			return { ok: false, intent, error: "Failed to check for test runs" };
		}

		if (testRunsCount && testRunsCount > 0) {
			return {
				ok: false,
				intent,
				error: "Cannot delete dataset that has been used in test runs",
			};
		}

		const { error: deleteEntriesError } = await supabase
			.from("test_data_entries")
			.delete()
			.eq("test_data_set_id", datasetId);

		if (deleteEntriesError) {
			return { ok: false, intent, error: "Failed to delete dataset entries" };
		}

		const { error: deleteError } = await supabase
			.from("test_data_sets")
			.delete()
			.eq("id", datasetId);

		if (deleteError) {
			return {
				ok: false,
				intent,
				error: deleteError.message ?? "Failed to delete dataset",
			};
		}

		return { ok: true, intent, datasetId };
	}

	return { ok: false, intent, error: "Unsupported intent" };
};
